import { Box } from '@mui/material';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';
import { feedPromptsModalStyles } from 'apps/studio/studio-tab/components/feed-prompts-modal/FeedPromptsModalStyles';

import ShowMoreComponent from 'apps/studio/studio-tab/components/ShowMoreComponent';
import { ReactComponent as Delete } from 'core/assets/svg/export-image/delete.svg';
import { ReactComponent as Download } from 'core/assets/svg/export-image/download.svg';

import { ReactComponent as CopyDetail } from 'core/assets/svg/modal-icons/copy-detail.svg';
import { ReactComponent as ReuseDark } from 'core/assets/svg/modal-icons/reuse-dark6.svg';
import { ReactComponent as Reuse } from 'core/assets/svg/modal-icons/reuse.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';

import { aspectRatioConfig } from 'core/config/AspectRatioConfig';
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';

import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { APP, HISTORY, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { setShowConfirmPromptScreen } from 'core/store/reducers/appReducer/AppSlice';
import { handleReusePrompt } from 'core/store/reducers/appReducer/appSliceThunk';

import {
	setRandomiseStudioPrompt,
	toggleMultiDeletePromptConfirmationModal,
} from 'core/store/reducers/studioReducer/StudioSlice';

import { copyToClipboard, downloadImage, formatNumberString } from 'core/utils/utils';
import React, { useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function ModelDetails({
	selectedPrompt,
	handleClose,
	feedPromptsModalType,
	hidePrompt,
	setHidePrompt,
	hideNegativePrompt,
	setHideNegativePrompt,
}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const disableButtons = useMemo(() => selectedPrompt.loading, [selectedPrompt.loading]);

	const details = [
		{
			label: 'Negative prompt',
			valueKey: 'negativePrompt',
		},
		{
			label: 'Sampler',
			valueKey: 'sampler',
			singleLine: true,
		},
		{
			label: 'Steps',
			valueKey: 'steps',
			singleLine: true,
		},
		{
			label: 'AI Guidance',
			valueKey: 'guidance',
			singleLine: true,
		},
		{
			label: 'Seed',
			valueKey: 'seed',
			singleLine: true,
		},
		{
			label: 'Aspect ratio',
			valueKey: 'aspectRatio',
			allowCopy: false,
			singleLine: true,
		},
	];

	return (
		<Box id="details-cont" sx={feedPromptsModalStyles.promptDetailsCont}>
			<Box
				id="details-scroll-wrapper"
				sx={[
					feedPromptsModalStyles.promptDetailsScrollWrapper,
					reusableStyles.verticalScrollBarStyles,
					{
						'&::-webkit-scrollbar-track': { backgroundColor: 'colors.dark6' },
						'&::-webkit-scrollbar-thumb': { border: `4px solid ${colorPalette.dark6}` },
					},
				]}
			>
				<Box id="prompt-details" sx={feedPromptsModalStyles.detailScreenPromptDetails}>
					<Box id={ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR} />
					<ActionsMenu
						image={selectedPrompt?.media}
						handleClose={handleClose}
						orientation={LayoutOrientation.HORIZONTAL}
						source={feedPromptsModalType}
						imageId={selectedPrompt?.generationId}
					/>
					<Box sx={[feedPromptsModalStyles.detailCard]}>
						<Box id="model-used" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Model used
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box sx={[reusableStyles.flexRow, { gap: '4px' }]}>
										<Box sx={feedPromptsModalStyles.detailCardModelImage}>
											<BaseImage src={selectedPrompt.modelImage} alt={selectedPrompt.modelLabel} />
										</Box>
										<BaseTypography variant="b2_caption" styles={{ color: 'colors.light2' }}>
											{selectedPrompt.modelLabel}
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box sx={[feedPromptsModalStyles.detailCard]}>
						<Box id="prompt" sx={[reusableStyles.flexCol]}>
							<Box
								sx={[
									reusableStyles.flexRow,
									{
										width: '100%',
										marginBottom: '6px',
										justifyContent: 'space-between',
									},
								]}
							>
								<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
									Prompt
								</BaseTypography>

								<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
									<Box
										sx={feedPromptsModalStyles.copyDetailButton}
										onClick={(e) => {
											e.stopPropagation();
											copyToClipboard(selectedPrompt.prompt);
											sendToast(ToastType.VALUE_COPIED);
										}}
									>
										<CopyDetail />
									</Box>
								</Box>
							</Box>
							<Box sx={[feedPromptsModalStyles.detailValue]}>
								<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
									{selectedPrompt && (
										<ShowMoreComponent
											hide={hidePrompt}
											value={selectedPrompt.prompt ?? ''}
											showMoreFunc={() => setHidePrompt(false)}
										/>
									)}
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[feedPromptsModalStyles.detailCard]}>
						{details.map((detail, index) => {
							let icon = null;
							let value = selectedPrompt ? selectedPrompt[detail?.valueKey] : '';

							if (selectedPrompt && detail?.valueKey === 'aspectRatio') {
								icon = aspectRatioConfig[selectedPrompt.aspectRatioType]?.icon;
								value = aspectRatioConfig[selectedPrompt.aspectRatioType]?.label;
							}
							if (selectedPrompt && detail?.valueKey === 'negativePrompt') {
								value = (
									<ShowMoreComponent
										hide={hideNegativePrompt}
										value={selectedPrompt.negativePrompt ?? ''}
										showMoreFunc={() => setHideNegativePrompt(false)}
									/>
								);
							}

							let singleLine = false;
							if (detail.singleLine !== undefined) singleLine = detail?.singleLine;

							let copyIcon = true;
							if (detail.allowCopy !== undefined) copyIcon = detail?.allowCopy;

							const detailValueComp = (
								<Box sx={[feedPromptsModalStyles.detailValue]}>
									{icon && icon}
									<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
										{selectedPrompt &&
											(detail?.valueKey === 'guidance' ? formatNumberString(value) : value)}
									</BaseTypography>
								</Box>
							);

							return (
								<React.Fragment key={detail?.label}>
									{index !== 0 && (
										<Box id="prompt-details-divider" sx={feedPromptsModalStyles.divider} />
									)}
									<Box id={detail?.label} sx={[reusableStyles.flexCol]}>
										<Box
											sx={[
												reusableStyles.flexRow,
												{
													width: '100%',
													marginBottom: '6px',
													justifyContent: 'space-between',
												},
											]}
										>
											<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
												{detail?.label}
											</BaseTypography>

											<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
												{singleLine && detailValueComp}
												{copyIcon && (
													<Box
														sx={feedPromptsModalStyles.copyDetailButton}
														onClick={(e) => {
															e.stopPropagation();
															sendToast(ToastType.VALUE_COPIED);
															copyToClipboard(selectedPrompt[detail?.valueKey]);
														}}
													>
														<CopyDetail />
													</Box>
												)}
											</Box>
										</Box>
										{!singleLine && detailValueComp}
									</Box>
								</React.Fragment>
							);
						})}
					</Box>
				</Box>
			</Box>
			<Box id="move-to-studio-cont-wrapper" sx={feedPromptsModalStyles.detailScreenButtonsCont}>
				{!selectedPrompt.published && (
					<BaseButton
						id="publish-prompt"
						styles={[
							feedPromptsModalStyles.buttonStyles,
							feedPromptsModalStyles.publishPromptButton,
						]}
						disabled={disableButtons}
						onClick={(e) => {
							e.stopPropagation();
							dispatch(setShowConfirmPromptScreen(true));
						}}
					>
						<BaseTypography variant="b1_bold" styles={{ color: 'inherit' }}>
							Publish
						</BaseTypography>
					</BaseButton>
				)}

				<Box sx={[reusableStyles.flexRow, { flexGrow: 1, gap: 'inherit' }]}>
					<BaseButton
						id="delete-button"
						disabled={disableButtons}
						styles={[feedPromptsModalStyles.buttonStyles, feedPromptsModalStyles.downloadButton]}
						onClick={() => {
							dispatch(
								toggleMultiDeletePromptConfirmationModal({
									open: true,
									prompt: [
										{
											generationId: selectedPrompt?.generationId,
											batchId: selectedPrompt?.batchId,
										},
									],
								}),
							);
						}}
					>
						<Delete />
					</BaseButton>
					<BaseButton
						id="download-button"
						disabled={disableButtons}
						styles={[feedPromptsModalStyles.buttonStyles, feedPromptsModalStyles.downloadButton]}
						onClick={() => {
							downloadImage(selectedPrompt?.image || '');
						}}
					>
						<Download />
					</BaseButton>
					<BaseButton
						id="reuse-prompt"
						styles={[
							feedPromptsModalStyles.buttonStyles,
							feedPromptsModalStyles.downloadButton,
							{
								backgroundColor: selectedPrompt.published ? 'colors.light1' : 'colors.dark3',
								'&:hover': {
									backgroundColor: selectedPrompt.published ? 'colors.light1' : 'colors.dark3',
								},
							},
						]}
						onClick={(e) => {
							e.stopPropagation();
							dispatch(handleReusePrompt());
							if (feedPromptsModalType === HISTORY) {
								navigate(`/${APP}/${TEXT_TO_IMAGE}`);
								// NOTE: SETTING RANDOMISE STUDIO PROMPT TO FALSE
								dispatch(setRandomiseStudioPrompt(false));
							}
							handleClose();
						}}
					>
						{selectedPrompt.published && (
							<BaseTypography variant="b1_bold" styles={{ color: 'inherit' }}>
								Reuse prompt
							</BaseTypography>
						)}
						{!selectedPrompt.published ? <Reuse /> : <ReuseDark />}
					</BaseButton>
				</Box>
			</Box>
		</Box>
	);
}
